import { ToggleRightIcon } from '@blocksuite/icons/lit';
import { cssVarV2 } from '@streamlain/theme/v2';
import { html, nothing, type TemplateResult } from 'lit';

import { themedScrollbarStyle } from '../styles';
import { STREAMLAIN_MOTION } from './streamlain-animation-controller';
import { streamLainDisclosureMotion } from './streamlain-disclosure-directive';

const feedbackTiming = `${STREAMLAIN_MOTION.feedback.duration}ms ${STREAMLAIN_MOTION.feedback.easing}`;
const resizeTiming = `${STREAMLAIN_MOTION.resize.duration}ms ${STREAMLAIN_MOTION.resize.easing}`;
import {
  STREAMLAIN_DISCLOSURE_OVERLAY_ENTER_ANIMATION,
  streamLainDisclosureOverlayKeyframesCss,
} from './streamlain-disclosure-motion';

const scrollableOverlaySelectors = [
  '.streamlain-disclosure-overlay-panel',
  '.streamlain-disclosure-popover-scroll',
  '.streamlain-display-list',
  '.streamlain-nested-display-list',
  '.canvas-context-list',
  '.canvas-context-portal',
  '.context-group-list',
  '.context-group-portal',
  '.doc-source-list',
  '.doc-source-portal',
];

const disclosureTriggerThemeVars = `
  --streamlain-disclosure-trigger-height: 28px;
  --streamlain-disclosure-trigger-radius: 6px;
  --streamlain-disclosure-trigger-padding-x: 6px;
  --streamlain-disclosure-trigger-border: ${cssVarV2.layer.insideBorder.border};
  --streamlain-disclosure-trigger-bg: ${cssVarV2.layer.background.primary};
  --streamlain-disclosure-trigger-bg-hover: ${cssVarV2.layer.background.hoverOverlay};
  --streamlain-disclosure-trigger-color: ${cssVarV2.text.primary};
  --streamlain-disclosure-trigger-icon: ${cssVarV2.icon.primary};
  --streamlain-disclosure-trigger-icon-secondary: ${cssVarV2.icon.secondary};
`;

export const streamLainDisclosurePopoverShellStyles = `
  .streamlain-disclosure-popover-shell {
    --streamlain-disclosure-viewport-padding: 16px;

    display: block;
    width: min(var(--streamlain-disclosure-overlay-width, 420px), calc(100vw - 32px));
    max-width: 100%;
    border: 0.5px solid var(--streamlain-v2-layer-insideBorder-border);
    border-radius: 8px;
    background: var(
      --streamlain-v2-layer-background-overlayPanel,
      var(--streamlain-v2-layer-background-primary)
    );
    background-clip: padding-box;
    box-shadow: var(
      --streamlain-v2-shadow-overlayPanel,
      0 12px 32px rgba(0, 0, 0, 0.18)
    );
    box-sizing: border-box;
    color: var(--streamlain-v2-text-primary);
    overflow: visible;
  }

  .streamlain-disclosure-popover-scroll {
    max-height: min(
      var(--streamlain-disclosure-popover-max-height, 520px),
      var(
        --streamlain-disclosure-popover-scroll-max-height,
        calc(
          100vh - var(--streamlain-disclosure-viewport-padding, 16px) -
            var(--streamlain-disclosure-viewport-padding, 16px)
        )
      )
    );
    min-height: 0;
    overflow: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
  }

  .streamlain-disclosure-popover-body {
    padding: 14px 16px;
  }

  .streamlain-disclosure-popover-compact-text {
    color: var(--streamlain-v2-text-secondary, #8e8e93);
    font-size: 12px;
    line-height: 1.35;
    opacity: 0.72;
  }

  .streamlain-disclosure-popover-compact-text chat-content-rich-text,
  .streamlain-disclosure-popover-compact-text chat-content-rich-text * {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .streamlain-disclosure-popover-compact-text p,
  .streamlain-disclosure-popover-compact-text ul,
  .streamlain-disclosure-popover-compact-text ol {
    margin-block: 4px;
  }

  .streamlain-disclosure-popover-compact-text h1,
  .streamlain-disclosure-popover-compact-text h2,
  .streamlain-disclosure-popover-compact-text h3,
  .streamlain-disclosure-popover-compact-text h4,
  .streamlain-disclosure-popover-compact-text h5,
  .streamlain-disclosure-popover-compact-text h6 {
    margin: 0 0 8px;
    color: var(--streamlain-v2-text-primary);
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
  }

  .streamlain-disclosure-popover-compact-text > :first-child,
  .streamlain-disclosure-popover-compact-text chat-content-rich-text > :first-child {
    margin-top: 0;
  }

  .streamlain-disclosure-popover-compact-text > :last-child,
  .streamlain-disclosure-popover-compact-text chat-content-rich-text > :last-child {
    margin-bottom: 0;
  }

  ${themedScrollbarStyle(['.streamlain-disclosure-popover-scroll'])}
`;

export const streamLainDisclosureStyles = `
  ${streamLainDisclosureOverlayKeyframesCss()}

  .streamlain-inline-disclosure {
    margin: 12px 0 0;
    width: min(420px, 100%);
    border: 0.5px solid ${cssVarV2.layer.insideBorder.border};
    border-radius: 8px;
    background: ${cssVarV2.layer.background.primary};
    overflow: visible;
    box-sizing: border-box;
  }

  .streamlain-disclosure-trigger-card {
    ${disclosureTriggerThemeVars}
    margin: 0;
    width: fit-content;
    max-width: 100%;
    min-width: 0;
    display: inline-flex;
    height: var(--streamlain-disclosure-trigger-height);
    align-items: center;
    gap: 6px;
    padding: 0 var(--streamlain-disclosure-trigger-padding-x);
    border: 0.5px solid var(--streamlain-disclosure-trigger-border);
    border-radius: var(--streamlain-disclosure-trigger-radius);
    background: var(--streamlain-disclosure-trigger-bg);
    color: var(--streamlain-disclosure-trigger-color);
    box-sizing: border-box;
    cursor: pointer;
    overflow: hidden;
  }

  .streamlain-disclosure-trigger-card[aria-expanded='true'] {
    border-color: ${cssVarV2.button.primary};
    background: var(--streamlain-disclosure-trigger-bg-hover);
    color: var(--streamlain-disclosure-trigger-color);
  }

  .streamlain-disclosure-trigger-card[data-clickable='false'] {
    cursor: default;
  }

  .streamlain-disclosure-trigger-card[data-clickable='false']:hover {
    border-color: var(--streamlain-disclosure-trigger-border);
    background: var(--streamlain-disclosure-trigger-bg);
    color: var(--streamlain-disclosure-trigger-color);
  }

  .streamlain-disclosure-pill .streamlain-inline-disclosure-trigger {
    max-width: 100%;
  }

  .streamlain-disclosure-pill-content {
    min-width: 0;
    max-width: 100%;
  }

  .streamlain-disclosure-pill-icon {
    display: inline-flex;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--streamlain-disclosure-trigger-icon);
  }

  .streamlain-disclosure-pill-icon svg {
    width: 16px;
    height: 16px;
  }

  .streamlain-disclosure-pill-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-disclosure-pill-meta {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    color: var(--streamlain-disclosure-trigger-icon-secondary);
    font-size: 11px;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-disclosure-trigger-card:hover {
    background: var(--streamlain-disclosure-trigger-bg-hover);
    color: var(--streamlain-disclosure-trigger-color);
  }

  .streamlain-inline-disclosure summary {
    list-style: none;
  }

  .streamlain-inline-disclosure summary::-webkit-details-marker {
    display: none;
  }

  .streamlain-inline-disclosure > .streamlain-inline-disclosure-body {
    display: grid;
    grid-template-rows: 1fr;
    opacity: 1;
  }

  .streamlain-inline-disclosure > .streamlain-inline-disclosure-body[hidden] {
    display: none;
  }

  .streamlain-inline-disclosure-body-inner {
    min-height: 0;
    overflow: hidden;
  }

  /* The divider belongs to the bordered disclosure card only; quiet lines that
     reuse the motion (the chat's activity and sources) open without one. */
  .streamlain-inline-disclosure
    > .streamlain-inline-disclosure-body
    > .streamlain-inline-disclosure-body-inner {
    border-top: 0.5px solid ${cssVarV2.layer.insideBorder.border};
  }

  .streamlain-inline-disclosure-trigger {
    width: 100%;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    min-height: 32px;
    padding: 7px 10px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: ${cssVarV2.text.primary};
    font-size: 12px;
    line-height: 18px;
    text-align: left;
    cursor: pointer;
    transition:
      background ${feedbackTiming},
      border-color ${feedbackTiming},
      color ${feedbackTiming};
  }

  .streamlain-disclosure-trigger-card .streamlain-inline-disclosure-trigger {
    min-height: 100%;
    padding: 0;
    border-radius: 0;
    color: var(--streamlain-disclosure-trigger-color);
    pointer-events: none;
  }

  .streamlain-inline-disclosure-trigger:hover {
    background: ${cssVarV2.layer.background.hoverOverlay};
    color: ${cssVarV2.text.primary};
  }

  .streamlain-disclosure-trigger-card:hover .streamlain-inline-disclosure-trigger {
    background: transparent;
    color: var(--streamlain-disclosure-trigger-color);
  }

  .streamlain-inline-disclosure[open] .streamlain-inline-disclosure-trigger {
    border-radius: 8px 8px 0 0;
  }

  .streamlain-inline-disclosure-chevron {
    display: inline-flex;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: currentColor;
    transform-origin: center;
  }

  .streamlain-inline-disclosure-chevron svg {
    width: 16px;
    height: 16px;
    display: block;
    flex: 0 0 auto;
  }

  .streamlain-inline-disclosure-summary {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-disclosure-overlay-panel {
    width: min(420px, calc(100vw - 32px));
    max-height: min(360px, calc(100vh - 96px));
    border: 0.5px solid ${cssVarV2.layer.insideBorder.border};
    border-radius: 8px;
    background: ${cssVarV2.layer.background.primary};
    background-clip: padding-box;
    box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.18),
      inset 0 0 0 0.5px ${cssVarV2.layer.insideBorder.border};
    overflow: auto;
    box-sizing: border-box;
    transform-origin: bottom right;
    animation: ${STREAMLAIN_DISCLOSURE_OVERLAY_ENTER_ANIMATION};
  }

  ${streamLainDisclosurePopoverShellStyles}

  ${themedScrollbarStyle(scrollableOverlaySelectors)}

  .streamlain-disclosure-overlay-anchor {
    position: relative;
    display: inline-flex;
    max-width: 100%;
    min-width: 0;
  }

  .streamlain-disclosure-overlay-popover {
    position: absolute;
    right: 0;
    bottom: calc(100% + var(--streamlain-disclosure-overlay-gap, 8px));
    z-index: var(--streamlain-z-index-popover, 9999);
    width: max-content;
    max-width: min(
      var(--streamlain-disclosure-overlay-width, 420px),
      calc(100vw - 32px)
    );
    transform: translateX(var(--streamlain-disclosure-overlay-shift-x, 0px));
  }

  .streamlain-disclosure-overlay-down .streamlain-disclosure-overlay-popover {
    top: calc(100% + var(--streamlain-disclosure-overlay-gap, 8px));
    bottom: auto;
  }

  .streamlain-disclosure-overlay-down .streamlain-disclosure-overlay-panel {
    transform-origin: top right;
  }

  .streamlain-disclosure-overlay-popover .streamlain-disclosure-overlay-panel {
    width: max-content;
    max-width: 100%;
  }

  .streamlain-disclosure-overlay-up
    .streamlain-disclosure-trigger-card[aria-expanded='true']
    .streamlain-inline-disclosure-chevron {
    transform: rotate(-90deg);
  }

  .streamlain-disclosure-overlay-down
    .streamlain-disclosure-trigger-card[aria-expanded='true']
    .streamlain-inline-disclosure-chevron {
    transform: rotate(90deg);
  }

  .streamlain-display-list {
    display: flex;
    flex-direction: column;
    gap: 1px;
    overflow-y: auto;
  }

  .streamlain-display-row {
    width: 100%;
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 8px 8px 8px calc(12px + var(--depth, 0) * 14px);
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: ${cssVarV2.text.primary};
    box-sizing: border-box;
    text-align: left;
  }

  .streamlain-display-row-clickable {
    cursor: pointer;
  }

  .streamlain-display-row-clickable:hover,
  .streamlain-display-row-clickable:focus-visible {
    background: ${cssVarV2.layer.background.hoverOverlay};
    outline: none;
  }

  .streamlain-display-row-icon {
    width: 20px;
    height: 20px;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${cssVarV2.icon.secondary};
    overflow: hidden;
  }

  .streamlain-display-row-icon svg {
    width: 16px;
    height: 16px;
  }

  .streamlain-display-row-leading-label {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: ${cssVarV2.text.primary};
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    line-height: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-display-row-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .streamlain-display-row-title,
  .streamlain-display-row-subtitle,
  .streamlain-display-row-preview {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-display-row-title {
    color: ${cssVarV2.text.primary};
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
  }

  .streamlain-display-row-subtitle,
  .streamlain-display-row-preview {
    color: ${cssVarV2.text.secondary};
    font-size: 11px;
    line-height: 16px;
  }

  .streamlain-display-row-action {
    min-width: 24px;
    display: inline-flex;
    justify-content: flex-end;
  }

  .streamlain-nested-display-list {
    display: flex;
    flex-direction: column;
    gap: 0;
    overflow-y: auto;
  }

  .streamlain-nested-display-group {
    margin: 0;
    padding: 0;
  }

  .streamlain-nested-display-group > summary {
    list-style: none;
  }

  .streamlain-nested-display-group > summary::-webkit-details-marker {
    display: none;
  }

  .streamlain-nested-display-group[open]
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron {
    transform: rotate(0deg);
  }

  .streamlain-nested-display-group[data-open='true']
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron {
    transform: rotate(0deg);
  }

  .streamlain-nested-display-group-body {
    display: grid;
    grid-template-rows: 0fr;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
    transition:
      grid-template-rows ${resizeTiming},
      opacity ${resizeTiming};
  }

  .streamlain-nested-display-group-body-inner {
    min-height: 0;
    overflow: hidden;
  }

  .streamlain-nested-display-group[data-open='true']
    > .streamlain-nested-display-group-body {
    grid-template-rows: 1fr;
    opacity: 1;
    pointer-events: auto;
  }

  .streamlain-nested-display-row {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 8px 8px 8px calc(12px + var(--depth, 0) * 14px);
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: ${cssVarV2.text.primary};
    box-sizing: border-box;
    text-align: left;
  }

  .streamlain-nested-display-row[data-has-toggle-space='true'] {
    grid-template-columns: 16px 20px minmax(0, 1fr) auto;
  }

  .streamlain-nested-display-row[data-toggle-style='plus-circle'] {
    grid-template-columns: 20px minmax(0, 1fr) auto;
  }

  .streamlain-nested-display-row[data-depth]::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: var(--tree-left, 20px);
    width: 1px;
    z-index: 0;
    background: ${cssVarV2.layer.insideBorder.border};
    pointer-events: none;
  }

  .streamlain-nested-display-row[data-depth]::after {
    content: '';
    position: absolute;
    top: calc(50% - var(--tree-curve-radius, 6px));
    left: var(--tree-left, 20px);
    width: var(--tree-connector-width, 6px);
    height: var(--tree-curve-radius, 6px);
    z-index: 0;
    border-bottom: 1px solid ${cssVarV2.layer.insideBorder.border};
    border-left: 1px solid ${cssVarV2.layer.insideBorder.border};
    border-bottom-left-radius: var(--tree-curve-radius, 6px);
    background: transparent;
    box-sizing: border-box;
    pointer-events: none;
  }

  .streamlain-nested-display-row[data-tree-last='true']::before {
    bottom: calc(50% + var(--tree-curve-radius, 6px));
  }

  .streamlain-nested-display-tree-guide {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: var(--tree-guide-left, 20px);
    width: 1px;
    background: ${cssVarV2.layer.insideBorder.border};
    pointer-events: none;
  }

  .streamlain-nested-display-row-clickable {
    cursor: pointer;
  }

  .streamlain-nested-display-row-clickable:hover,
  .streamlain-nested-display-row-clickable:focus-visible {
    background: ${cssVarV2.layer.background.hoverOverlay};
    outline: none;
  }

  .streamlain-nested-display-chevron-slot,
  .streamlain-nested-display-icon {
    position: relative;
    z-index: 1;
    width: 20px;
    height: 20px;
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    color: ${cssVarV2.icon.secondary};
  }

  .streamlain-nested-display-chevron-slot {
    width: 20px;
    height: 20px;
  }

  .streamlain-nested-display-row[data-toggle-style='plus-circle']
    .streamlain-nested-display-chevron-slot {
    width: 20px;
    height: 20px;
  }

  .streamlain-nested-display-row[data-toggle-style='plus-circle']
    .streamlain-nested-display-icon {
    display: none;
  }

  .streamlain-nested-display-chevron {
    position: relative;
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: ${cssVarV2.icon.secondary};
    transform: rotate(-90deg);
    transition:
      transform ${feedbackTiming},
      background ${feedbackTiming};
  }

  .streamlain-nested-display-chevron[data-expanded='true'] {
    transform: rotate(0deg);
  }

  .streamlain-nested-display-chevron:hover {
    background: ${cssVarV2.layer.background.hoverOverlay};
  }

  .streamlain-nested-display-chevron[data-toggle-style='plus-circle'] {
    width: 20px;
    height: 20px;
    border: 0.5px solid currentColor;
    border-radius: 999px;
    background: var(
      --streamlain-v2-layer-background-overlayPanel,
      ${cssVarV2.layer.background.primary}
    );
    box-sizing: border-box;
    transform: none;
  }

  .streamlain-nested-display-icon {
    border: 0.5px solid currentColor;
    border-radius: 999px;
    background: var(
      --streamlain-v2-layer-background-overlayPanel,
      ${cssVarV2.layer.background.primary}
    );
    box-sizing: border-box;
  }

  .streamlain-nested-display-group[open]
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron[data-toggle-style='plus-circle'] {
    transform: none;
  }

  .streamlain-nested-display-group[data-open='true']
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron[data-toggle-style='plus-circle'] {
    transform: none;
  }

  .streamlain-nested-display-chevron[data-toggle-style='plus-circle'] svg {
    display: none;
  }

  .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::before,
  .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    border-radius: 999px;
    background: currentColor;
    transform: translate(-50%, -50%);
    transition:
      opacity ${feedbackTiming},
      transform ${feedbackTiming};
  }

  .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::before {
    width: 7px;
    height: 1.5px;
  }

  .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::after {
    width: 1.5px;
    height: 7px;
  }

  .streamlain-nested-display-group[open]
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::after {
    opacity: 0;
    transform: translate(-50%, -50%) scaleY(0);
  }

  .streamlain-nested-display-group[data-open='true']
    > .streamlain-nested-display-row
    .streamlain-nested-display-chevron[data-toggle-style='plus-circle']::after {
    opacity: 0;
    transform: translate(-50%, -50%) scaleY(0);
  }

  .streamlain-nested-display-chevron svg,
  .streamlain-nested-display-icon svg {
    width: 16px;
    height: 16px;
  }

  .streamlain-nested-display-main {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .streamlain-nested-display-title,
  .streamlain-nested-display-subtitle,
  .streamlain-nested-display-preview {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .streamlain-nested-display-title {
    color: ${cssVarV2.text.primary};
    font-size: 13px;
    line-height: 18px;
    font-weight: 500;
  }

  .streamlain-nested-display-subtitle,
  .streamlain-nested-display-preview {
    color: ${cssVarV2.text.secondary};
    font-size: 11px;
    line-height: 16px;
  }

  .streamlain-nested-display-action {
    min-width: 24px;
    display: inline-flex;
    justify-content: flex-end;
  }

  @media (prefers-reduced-motion: reduce) {
    .streamlain-disclosure-overlay-panel { animation: none; }
    .streamlain-inline-disclosure-trigger,
    .streamlain-inline-disclosure-chevron,
    .streamlain-nested-display-group-body,
    .streamlain-nested-display-chevron,
    .streamlain-nested-display-chevron::before,
    .streamlain-nested-display-chevron::after { transition: none; }
  }

`;

export interface StreamLainDisclosureOptions {
  className?: string;
  content: unknown;
  onToggle?: (event: Event) => void;
  open?: boolean;
  summary: unknown;
}

export interface StreamLainDisclosureTriggerOptions {
  className?: string;
  controlsId?: string;
  expanded?: boolean;
  onClick?: (event: MouseEvent) => void;
  summary: unknown;
  title?: string;
}

export interface StreamLainDisclosurePillOptions {
  ariaHasPopup?:
    | 'false'
    | 'true'
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog';
  className?: string;
  controlsId?: string;
  expanded?: boolean;
  expandable?: boolean;
  icon?: unknown;
  label: unknown;
  meta?: unknown;
  onClick?: (event: MouseEvent) => void;
  title?: string;
}

export interface StreamLainDisclosureOverlayOptions {
  className?: string;
  content: unknown;
  id?: string;
}

export interface StreamLainDisplayItemRowOptions {
  action?: unknown;
  className?: string;
  depth?: number;
  icon?: unknown;
  onClick?: (event: MouseEvent) => void;
  preview?: unknown;
  subtitle?: unknown;
  title: unknown;
  titleAttr?: string;
}

export interface StreamLainNestedDisplayItemRowOptions extends StreamLainDisplayItemRowOptions {
  clickSurface?: 'button' | 'div';
  element?: 'div' | 'button' | 'summary';
  expanded?: boolean;
  reserveToggleSpace?: boolean;
  treeGuideDepths?: number[];
  treeLast?: boolean;
  treeToggleStyle?: 'chevron' | 'plus-circle';
}

function handleKeyboardClick(
  event: KeyboardEvent,
  onClick?: (event: MouseEvent) => void
) {
  if (!onClick || (event.key !== 'Enter' && event.key !== ' ')) {
    return;
  }

  event.preventDefault();
  onClick(event as unknown as MouseEvent);
}

export function syncStreamLainDisclosureOverlayGeometry({
  boundary,
  host,
  popoverSelector = '.streamlain-disclosure-overlay-popover',
  trigger,
}: {
  boundary: Element;
  host: HTMLElement;
  popoverSelector?: string;
  trigger: HTMLElement;
}) {
  const boundaryRect = boundary.getBoundingClientRect();
  const triggerRect = trigger.getBoundingClientRect();
  const viewportSafeWidth = Math.max(0, window.innerWidth - 32);
  const overlayMaxWidth =
    boundaryRect.width > 0
      ? Math.min(420, Math.max(0, boundaryRect.width))
      : Math.min(420, viewportSafeWidth);

  host.style.setProperty(
    '--streamlain-disclosure-overlay-width',
    `${overlayMaxWidth}px`
  );

  const popover = host.querySelector<HTMLElement>(popoverSelector);
  const measuredOverlayWidth = popover?.getBoundingClientRect().width ?? 0;
  const overlayWidth =
    measuredOverlayWidth > 0
      ? Math.min(overlayMaxWidth, measuredOverlayWidth)
      : overlayMaxWidth;
  const minLeft = boundaryRect.width > 0 ? boundaryRect.left : 16;
  const maxLeft =
    boundaryRect.width > 0
      ? boundaryRect.right - overlayWidth
      : window.innerWidth - 16 - overlayWidth;
  const desiredLeft = triggerRect.right - overlayWidth;
  const clampedLeft = Math.min(
    Math.max(desiredLeft, minLeft),
    Math.max(minLeft, maxLeft)
  );
  const shiftX = clampedLeft - desiredLeft;

  host.style.setProperty(
    '--streamlain-disclosure-overlay-shift-x',
    `${shiftX}px`
  );
}

export function renderStreamLainDisplayItemLeadingLabel(
  label: string
): TemplateResult<1> | typeof nothing {
  return label
    ? html`<span class="streamlain-display-row-leading-label" title=${label}
        >${label}</span
      >`
    : nothing;
}

export function renderStreamLainNestedDisplayItemRow({
  action,
  clickSurface = 'button',
  className,
  depth = 0,
  element = 'div',
  expanded,
  icon,
  onClick,
  preview,
  reserveToggleSpace = false,
  subtitle,
  title,
  titleAttr,
  treeGuideDepths = [],
  treeLast = false,
  treeToggleStyle = 'chevron',
}: StreamLainNestedDisplayItemRowOptions): TemplateResult<1> {
  const normalizedDepth = Math.max(0, depth);
  const normalizedGuideDepths = [
    ...new Set(
      treeGuideDepths.filter(
        guideDepth => guideDepth > 0 && guideDepth < normalizedDepth
      )
    ),
  ];
  const rowClass = [
    'streamlain-nested-display-row',
    onClick || element === 'summary'
      ? 'streamlain-nested-display-row-clickable'
      : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const hasChevron = expanded !== undefined;
  const hasToggleSpace = hasChevron || reserveToggleSpace;
  const content = html`
    ${normalizedGuideDepths.map(
      guideDepth =>
        html`<span
          class="streamlain-nested-display-tree-guide"
          aria-hidden="true"
          style=${`--tree-guide-left:${20 + Math.max(0, guideDepth - 1) * 14}px`}
        ></span>`
    )}
    ${hasToggleSpace
      ? html`<span
          class="streamlain-nested-display-chevron-slot"
          aria-hidden="true"
        >
          ${hasChevron
            ? html`<span
                class="streamlain-nested-display-chevron"
                data-expanded=${expanded ? 'true' : 'false'}
                data-toggle-style=${treeToggleStyle}
              >
                <svg viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4.5 6.25 8 9.75l3.5-3.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </span>`
            : nothing}
        </span>`
      : nothing}
    <span class="streamlain-nested-display-icon" aria-hidden="true"
      >${icon}</span
    >
    <span class="streamlain-nested-display-main">
      <span class="streamlain-nested-display-title">${title}</span>
      ${subtitle
        ? html`<span class="streamlain-nested-display-subtitle"
            >${subtitle}</span
          >`
        : nothing}
      ${preview
        ? html`<span class="streamlain-nested-display-preview"
            >${preview}</span
          >`
        : nothing}
    </span>
    <span class="streamlain-nested-display-action">${action}</span>
  `;
  const attrs = {
    class: rowClass,
    style: `--depth:${normalizedDepth};--tree-left:${20 + Math.max(0, normalizedDepth - 1) * 14}px;--tree-connector-width:6px;--tree-curve-radius:6px`,
    title: titleAttr ?? '',
    dataHasChevron: hasChevron ? 'true' : nothing,
    dataHasToggleSpace: hasToggleSpace ? 'true' : nothing,
    dataDepth: normalizedDepth > 0 ? String(normalizedDepth) : nothing,
    dataTreeLast: treeLast ? 'true' : nothing,
    dataToggleStyle: hasChevron ? treeToggleStyle : nothing,
    ariaExpanded: hasChevron ? String(Boolean(expanded)) : nothing,
  };

  if (element === 'summary') {
    return html`<summary
      class=${attrs.class}
      style=${attrs.style}
      title=${attrs.title}
      data-has-chevron=${attrs.dataHasChevron}
      data-has-toggle-space=${attrs.dataHasToggleSpace}
      data-depth=${attrs.dataDepth}
      data-tree-last=${attrs.dataTreeLast}
      data-toggle-style=${attrs.dataToggleStyle}
      aria-expanded=${attrs.ariaExpanded}
    >
      ${content}
    </summary>`;
  }

  if (onClick && clickSurface === 'div') {
    return html`<div
      class=${attrs.class}
      style=${attrs.style}
      title=${attrs.title}
      role="button"
      tabindex="0"
      data-has-chevron=${attrs.dataHasChevron}
      data-has-toggle-space=${attrs.dataHasToggleSpace}
      data-depth=${attrs.dataDepth}
      data-tree-last=${attrs.dataTreeLast}
      data-toggle-style=${attrs.dataToggleStyle}
      aria-expanded=${attrs.ariaExpanded}
      @click=${(event: MouseEvent) => onClick(event)}
      @keydown=${(event: KeyboardEvent) => handleKeyboardClick(event, onClick)}
    >
      ${content}
    </div>`;
  }

  if (onClick || element === 'button') {
    return html`<button
      type="button"
      class=${attrs.class}
      style=${attrs.style}
      title=${attrs.title}
      data-has-chevron=${attrs.dataHasChevron}
      data-has-toggle-space=${attrs.dataHasToggleSpace}
      data-depth=${attrs.dataDepth}
      data-tree-last=${attrs.dataTreeLast}
      data-toggle-style=${attrs.dataToggleStyle}
      aria-expanded=${attrs.ariaExpanded}
      @click=${(event: MouseEvent) => onClick?.(event)}
    >
      ${content}
    </button>`;
  }

  return html`<div
    class=${attrs.class}
    style=${attrs.style}
    title=${attrs.title}
    data-has-chevron=${attrs.dataHasChevron}
    data-has-toggle-space=${attrs.dataHasToggleSpace}
    data-depth=${attrs.dataDepth}
    data-tree-last=${attrs.dataTreeLast}
    data-toggle-style=${attrs.dataToggleStyle}
  >
    ${content}
  </div>`;
}

function renderStreamLainDisclosureTriggerContent(summary: unknown) {
  return html`
    <span class="streamlain-inline-disclosure-chevron" aria-hidden="true"
      >${ToggleRightIcon({ width: '16px', height: '16px' })}</span
    >
    <span class="streamlain-inline-disclosure-summary">${summary}</span>
  `;
}

function renderStreamLainDisclosurePillContent({
  expandable,
  icon,
  label,
  meta,
}: Pick<
  StreamLainDisclosurePillOptions,
  'expandable' | 'icon' | 'label' | 'meta'
>) {
  return html`
    ${expandable
      ? html`<span
          class="streamlain-inline-disclosure-chevron"
          aria-hidden="true"
          >${ToggleRightIcon({ width: '16px', height: '16px' })}</span
        >`
      : nothing}
    ${icon
      ? html`<span class="streamlain-disclosure-pill-icon" aria-hidden="true"
          >${icon}</span
        >`
      : nothing}
    <span class="streamlain-disclosure-pill-label">${label}</span>
    ${meta
      ? html`<span class="streamlain-disclosure-pill-meta">${meta}</span>`
      : nothing}
  `;
}

function renderStreamLainDisclosureBody({
  content,
  summary,
}: Pick<StreamLainDisclosureOptions, 'content' | 'summary'>) {
  return html`
    <summary class="streamlain-inline-disclosure-trigger">
      ${renderStreamLainDisclosureTriggerContent(summary)}
    </summary>
    <div class="streamlain-inline-disclosure-body">
      <div class="streamlain-inline-disclosure-body-inner">${content}</div>
    </div>
  `;
}

export function renderStreamLainDisclosureTrigger({
  className,
  controlsId,
  expanded = false,
  onClick,
  summary,
  title,
}: StreamLainDisclosureTriggerOptions): TemplateResult<1> {
  const buttonClass = ['streamlain-disclosure-trigger-card', className]
    .filter(Boolean)
    .join(' ');
  return html`<button
    type="button"
    class=${buttonClass}
    aria-expanded=${String(expanded)}
    aria-controls=${controlsId ?? nothing}
    title=${title ?? ''}
    @click=${(event: MouseEvent) => onClick?.(event)}
  >
    <span class="streamlain-inline-disclosure-trigger">
      ${renderStreamLainDisclosureTriggerContent(summary)}
    </span>
  </button>`;
}

export function renderStreamLainDisclosurePill({
  ariaHasPopup,
  className,
  controlsId,
  expanded = false,
  expandable = false,
  icon,
  label,
  meta,
  onClick,
  title,
}: StreamLainDisclosurePillOptions): TemplateResult<1> {
  const pillClass = [
    'streamlain-disclosure-trigger-card',
    'streamlain-disclosure-pill',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const content = html`<span
    class="streamlain-inline-disclosure-trigger streamlain-disclosure-pill-content"
  >
    ${renderStreamLainDisclosurePillContent({ expandable, icon, label, meta })}
  </span>`;

  if (!onClick) {
    return html`<span
      class=${pillClass}
      aria-expanded=${expandable ? String(expanded) : nothing}
      aria-controls=${controlsId ?? nothing}
      aria-haspopup=${ariaHasPopup ?? nothing}
      data-clickable="false"
      title=${title ?? ''}
    >
      ${content}
    </span>`;
  }

  return html`<button
    type="button"
    class=${pillClass}
    aria-expanded=${expandable ? String(expanded) : nothing}
    aria-controls=${controlsId ?? nothing}
    aria-haspopup=${ariaHasPopup ?? nothing}
    title=${title ?? ''}
    @click=${(event: MouseEvent) => onClick(event)}
  >
    ${content}
  </button>`;
}

export function renderStreamLainDisclosureOverlay({
  className,
  content,
  id,
}: StreamLainDisclosureOverlayOptions): TemplateResult<1> {
  const panelClass = ['streamlain-disclosure-overlay-panel', className]
    .filter(Boolean)
    .join(' ');
  return html`<div id=${id ?? nothing} class=${panelClass}>${content}</div>`;
}

export function renderStreamLainDisplayItemRow({
  action,
  className,
  depth = 0,
  icon,
  onClick,
  preview,
  subtitle,
  title,
  titleAttr,
}: StreamLainDisplayItemRowOptions): TemplateResult<1> {
  const rowClass = [
    'streamlain-display-row',
    onClick ? 'streamlain-display-row-clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  const content = html`
    <span class="streamlain-display-row-icon" aria-hidden="true">${icon}</span>
    <span class="streamlain-display-row-main">
      <span class="streamlain-display-row-title">${title}</span>
      ${subtitle
        ? html`<span class="streamlain-display-row-subtitle">${subtitle}</span>`
        : null}
      ${preview
        ? html`<span class="streamlain-display-row-preview">${preview}</span>`
        : null}
    </span>
    <span class="streamlain-display-row-action">${action}</span>
  `;

  if (onClick) {
    return html`<button
      type="button"
      class=${rowClass}
      style=${`--depth:${Math.max(0, depth)}`}
      title=${titleAttr ?? ''}
      @click=${onClick}
    >
      ${content}
    </button>`;
  }

  return html`<div
    class=${rowClass}
    style=${`--depth:${Math.max(0, depth)}`}
    title=${titleAttr ?? ''}
  >
    ${content}
  </div>`;
}

export function renderStreamLainDisclosure({
  className,
  content,
  onToggle,
  open,
  summary,
}: StreamLainDisclosureOptions): TemplateResult<1> {
  const detailsClass = ['streamlain-inline-disclosure', className]
    .filter(Boolean)
    .join(' ');

  if (open === undefined && onToggle === undefined) {
    return html`<details ${streamLainDisclosureMotion()} class=${detailsClass}>
      ${renderStreamLainDisclosureBody({ content, summary })}
    </details>`;
  }

  return html`<details
    ${streamLainDisclosureMotion()}
    class=${detailsClass}
    ?open=${Boolean(open)}
    @toggle=${onToggle}
  >
    ${renderStreamLainDisclosureBody({ content, summary })}
  </details>`;
}
